import { Injectable, isDevMode } from '@angular/core';
import { Routes } from '@angular/router';
import { FORTARIS_MODULOS, FORTARIS_MODULOS_CADASTRO } from './fortaris-modulos';

interface ModuloRotaInfo {
  modulo: string;
  path: string;
  permissao?: string | string[];
}

interface RelatorioNavegacao {
  modulosOficiais: string[];
  modulosEncontrados: string[];
  modulosMenu: string[];
  modulosSemMenu: string[];
  itensMenuInvalidos: string[];
  rotasSemPermissao: string[];
  modulosOficiaisNaoImplementados: string[];
}

@Injectable({ providedIn: 'root' })
export class NavigationValidationService {
  validar(routes: Routes): RelatorioNavegacao {
    const modulosOficiais = Array.from(
      new Set([...FORTARIS_MODULOS, ...FORTARIS_MODULOS_CADASTRO].map((modulo) => modulo.modulo)),
    );
    const modulosMenu = Array.from(
      new Set(
        [...FORTARIS_MODULOS, ...FORTARIS_MODULOS_CADASTRO]
          .filter((modulo) => modulo.visivelMenu)
          .map((modulo) => modulo.modulo),
      ),
    );
    const itensMenuRoutes = new Set(
      [...FORTARIS_MODULOS, ...FORTARIS_MODULOS_CADASTRO]
        .filter((modulo) => modulo.visivelMenu)
        .map((modulo) => modulo.rota),
    );

    const rotasModulo = this.coletarRotasModulo(routes);
    const modulosEncontrados = Array.from(new Set(rotasModulo.map((rota) => rota.modulo)));

    const modulosSemMenu = modulosEncontrados.filter((modulo) => !modulosMenu.includes(modulo));

    const rotasSemPermissao = rotasModulo
      .filter((rota) => !rota.permissao)
      .map((rota) => `${rota.modulo}:${rota.path}`);

    const rotasExistentes = new Set(this.coletarRotas(routes));
    const itensMenuInvalidos = Array.from(itensMenuRoutes).filter((rota) => !rotasExistentes.has(rota));

    const modulosOficiaisNaoImplementados = modulosOficiais.filter((modulo) => !modulosEncontrados.includes(modulo));

    const relatorio: RelatorioNavegacao = {
      modulosOficiais,
      modulosEncontrados,
      modulosMenu,
      modulosSemMenu,
      itensMenuInvalidos,
      rotasSemPermissao,
      modulosOficiaisNaoImplementados,
    };

    if (isDevMode()) {
      this.exibirRelatorio(relatorio);
    }

    return relatorio;
  }

  private coletarRotasModulo(routes: Routes, basePath = ''): ModuloRotaInfo[] {
    const resultado: ModuloRotaInfo[] = [];
    routes.forEach((route) => {
      const path = this.joinPath(basePath, route.path ?? '');
      if (route.data?.['modulo']) {
        resultado.push({
          modulo: route.data['modulo'] as string,
          path: path || '/',
          permissao: route.data['permissao'] as string | string[] | undefined,
        });
      }
      if (route.children?.length) {
        resultado.push(...this.coletarRotasModulo(route.children, path));
      }
    });
    return resultado;
  }

  private coletarRotas(routes: Routes, basePath = ''): string[] {
    const rotas: string[] = [];
    routes.forEach((route) => {
      if (route.path === '**') {
        return;
      }
      const path = this.joinPath(basePath, route.path ?? '');
      if (route.path !== undefined) {
        rotas.push(path || '/');
      }
      if (route.children?.length) {
        rotas.push(...this.coletarRotas(route.children, path));
      }
    });
    return rotas;
  }

  private joinPath(basePath: string, path: string): string {
    const cleanBase = basePath === '/' ? '' : basePath;
    if (!path) {
      return cleanBase;
    }
    if (!cleanBase) {
      return `/${path}`;
    }
    return `${cleanBase}/${path}`.replace(/\/+/, '/');
  }

  private exibirRelatorio(relatorio: RelatorioNavegacao): void {
    console.groupCollapsed('%cFortaris Navegação - Relatório de Consistência', 'color: #123C4A; font-weight: 600;');
    console.table({
      modulosOficiais: relatorio.modulosOficiais.length,
      modulosEncontrados: relatorio.modulosEncontrados.length,
      modulosMenu: relatorio.modulosMenu.length,
      modulosSemMenu: relatorio.modulosSemMenu.length,
      itensMenuInvalidos: relatorio.itensMenuInvalidos.length,
      rotasSemPermissao: relatorio.rotasSemPermissao.length,
      modulosOficiaisNaoImplementados: relatorio.modulosOficiaisNaoImplementados.length,
    });

    if (relatorio.modulosSemMenu.length) {
      console.warn('Módulos criados sem menu:', relatorio.modulosSemMenu);
    }
    if (relatorio.itensMenuInvalidos.length) {
      console.warn('Itens de menu com rota inválida:', relatorio.itensMenuInvalidos);
    }
    if (relatorio.rotasSemPermissao.length) {
      console.warn('Rotas de módulo sem permissão configurada:', relatorio.rotasSemPermissao);
    }
    if (relatorio.modulosOficiaisNaoImplementados.length) {
      console.warn('Módulos oficiais ainda não implementados:', relatorio.modulosOficiaisNaoImplementados);
    }
    console.groupEnd();
  }
}
